import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Phone, MapPin } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHead, Section, CtaBand } from "@/components/Shared";
import { UI } from "@/i18n/ui";
import { SITE, waLink } from "@/data/site";

const inputCls = "w-full rounded-xl border border-[#E5E1D8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6B53]/40 focus:border-[#4A6B53]";

const FormField = ({ label, value, onChange, type = "text", textarea, required, testId }) => (
  <div>
    <label className="text-sm font-medium text-[#1F2937]">{label}</label>
    {textarea ? (
      <textarea required={required} rows={4} className={inputCls} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input required={required} type={type} className={inputCls} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const ContactCards = ({ lang, m }) => {
  const cards = [
    { Icon: MessageCircle, title: "WhatsApp", sub: SITE.phone, href: waLink(lang === "id" ? "Halo Bali Empower" : "Hello Bali Empower"), testId: "contact-whatsapp", external: true },
    { Icon: Phone, title: m.phone, sub: SITE.phone, href: `tel:${SITE.phoneIntl}`, testId: "contact-phone" },
    { Icon: MapPin, title: m.address, sub: `${SITE.address.street}, ${SITE.address.area}, ${SITE.address.city}, ${SITE.address.region} ${SITE.address.postal}`, href: SITE.mapsUrl, testId: "contact-maps", external: true },
  ];
  return (
    <div className="space-y-5">
      {cards.map(({ Icon, title, sub, href, testId, external }) => (
        <a key={testId} href={href} data-testid={testId}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="be-card flex items-center gap-4 rounded-2xl border border-[#E5E1D8] bg-white p-6">
          <Icon size={22} className="text-[#4A6B53]" />
          <div>
            <p className="font-semibold text-[#1F2937]">{title}</p>
            <p className="text-sm text-[#4B5563]">{sub}</p>
          </div>
        </a>
      ))}
    </div>
  );
};

export default function Contact({ lang }) {
  const t = UI[lang];
  const m = t.misc;
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const meta = {
    id: { title: "Kontak Bali Empower — Klinik Psikologi Denpasar", desc: "Hubungi Bali Empower di Renon, Denpasar Selatan melalui WhatsApp 087777430348, telepon, atau formulir kontak. Tim kami siap membantu Anda menemukan dukungan yang sesuai." },
    en: { title: "Contact Bali Empower — Psychology Clinic Denpasar", desc: "Contact Bali Empower in Renon, Denpasar Selatan via WhatsApp 087777430348, phone, or the contact form. Our team is ready to help you find the right support." },
  }[lang];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) return;
    setSending(true);
    try {
      const message = lang === "id"
        ? `Halo Bali Empower, saya ${form.name}.\nTelepon: ${form.phone}\nEmail: ${form.email || "-"}\nPesan: ${form.message}`
        : `Hello Bali Empower, my name is ${form.name}.\nPhone: ${form.phone}\nEmail: ${form.email || "-"}\nMessage: ${form.message}`;
      window.open(waLink(message), "_blank", "noopener,noreferrer");
      toast.success(lang === "id" ? "WhatsApp dibuka. Silakan kirim pesan Anda." : "WhatsApp opened. Please send your message.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } finally {
      setSending(false);
    }
  };

  const input = inputCls;

  return (
    <div data-testid="contact-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.contact} title={m.contactTitle} />
      <Section className="pt-0">
        <div className="grid lg:grid-cols-2 gap-12">
          <ContactCards lang={lang} m={m} />
          <form onSubmit={submit} className="rounded-2xl border border-[#E5E1D8] bg-white p-8 space-y-4" data-testid="contact-form">
            <FormField required label={m.name} value={form.name} testId="contact-name-input" onChange={(v) => setForm({ ...form, name: v })} />
            <FormField required label={m.phoneField} value={form.phone} testId="contact-phone-input" onChange={(v) => setForm({ ...form, phone: v })} />
            <FormField label={m.email} type="email" value={form.email} testId="contact-email-input" onChange={(v) => setForm({ ...form, email: v })} />
            <FormField required textarea label={m.message} value={form.message} testId="contact-message-input" onChange={(v) => setForm({ ...form, message: v })} />
            <button type="submit" disabled={sending} data-testid="contact-submit-btn"
              className="w-full rounded-full bg-[#4A6B53] text-white font-medium py-3.5 hover:bg-[#3A5542] transition-colors disabled:opacity-60">
              {sending ? "..." : m.send}
            </button>
          </form>
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}
